import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import GavelIcon from '@mui/icons-material/Gavel';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase';
import { Worker } from '../../../types/worker';

interface DisciplinaryManagementProps {
  farmId: string;
  workers: Worker[];
}

interface DisciplinaryRecord {
  id: string;
  workerId: string;
  incidentDate: string;
  type: 'warning' | 'suspension' | 'termination' | 'other';
  description: string;
  actionTaken: string;
}

const DisciplinaryManagement: React.FC<DisciplinaryManagementProps> = ({ farmId, workers }) => {
  const [records, setRecords] = useState<DisciplinaryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  
  const [newRecord, setNewRecord] = useState<Partial<DisciplinaryRecord>>({
    type: 'warning'
  });

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('disciplinary_records')
        .select('*')
        .eq('farm_id', farmId)
        .order('incident_date', { ascending: false });

      if (error) throw error;

      const mapped = (data || []).map((r: any) => ({
        id: r.id,
        workerId: r.worker_id,
        incidentDate: r.incident_date,
        type: r.type,
        description: r.description,
        actionTaken: r.action_taken
      }));

      setRecords(mapped);
    } catch (err: any) {
      console.error('Error fetching disciplinary records:', err);
      setError('Failed to load disciplinary records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (farmId) fetchRecords();
  }, [farmId]);

  const handleSubmit = async () => {
    try {
      if (!newRecord.workerId || !newRecord.incidentDate || !newRecord.type) {
        setError('Please fill in required fields');
        return;
      }

      const { error } = await supabase
        .from('disciplinary_records')
        .insert([{
          farm_id: farmId,
          worker_id: newRecord.workerId,
          incident_date: newRecord.incidentDate,
          type: newRecord.type,
          description: newRecord.description,
          action_taken: newRecord.actionTaken
        }]);

      if (error) throw error;
      
      setOpenDialog(false);
      setNewRecord({ type: 'warning' });
      await fetchRecords();
    } catch (err: any) {
      console.error('Error adding record:', err);
      setError('Failed to add disciplinary record');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from('disciplinary_records').delete().eq('id', id);
      if (error) throw error;
      await fetchRecords();
    } catch (err) {
      console.error('Error deleting record:', err);
    }
  };

  const getWorkerName = (id: string) => {
    return workers.find(w => w.id === id)?.name || 'Unknown';
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'warning': return 'warning';
      case 'suspension': return 'error';
      case 'termination': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Disciplinary Records</Typography>
        <Button 
          variant="contained" 
          color="error"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Record
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card variant="outlined">
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Worker</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Action Taken</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No disciplinary records found</TableCell>
                  </TableRow>
                ) : (
                  records.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{format(new Date(r.incidentDate), 'MMM d, yyyy')}</TableCell>
                      <TableCell>{getWorkerName(r.workerId)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={r.type} 
                          size="small" 
                          color={getTypeColor(r.type) as any}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>{r.description}</TableCell>
                      <TableCell>{r.actionTaken || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleDelete(r.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Disciplinary Record</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Worker"
              fullWidth
              value={newRecord.workerId || ''}
              onChange={(e) => setNewRecord({ ...newRecord, workerId: e.target.value })}
            >
              {workers.map((worker) => (
                <MenuItem key={worker.id} value={worker.id}>{worker.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              type="date"
              label="Incident Date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={newRecord.incidentDate || ''}
              onChange={(e) => setNewRecord({ ...newRecord, incidentDate: e.target.value })}
            />

            <TextField
              select
              label="Type"
              fullWidth
              value={newRecord.type || 'warning'}
              onChange={(e) => setNewRecord({ ...newRecord, type: e.target.value as any })}
            >
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="suspension">Suspension</MenuItem>
              <MenuItem value="termination">Termination</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>

            <TextField
              label="Description"
              multiline
              rows={3}
              fullWidth
              value={newRecord.description || ''}
              onChange={(e) => setNewRecord({ ...newRecord, description: e.target.value })}
            />

            <TextField
              label="Action Taken"
              multiline
              rows={2}
              fullWidth
              value={newRecord.actionTaken || ''}
              onChange={(e) => setNewRecord({ ...newRecord, actionTaken: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="error">Save Record</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DisciplinaryManagement;
